import { useState, useCallback, useEffect, useRef, type ChangeEvent, type FormEvent } from 'react';
import {
  useResetRollMutation,
  useDeleteRollMutation,
  useConfirmRollMutation,
  useUpdateRollNotesMutation,
  useUpdateRollEmptyMutation,
  useRenameRollMutation,
} from '../hooks/useRolls';
import { useConfirm } from '../hooks/useConfirm';
import { useFileDrop } from '../hooks/useFileDrop';
import { useRollUploads } from '../contexts/UploadQueueContext';
import { ACTIVE_STATUSES, type UploadJob } from '../uploads/uploadQueue';
import { acceptFiles, describeRejections, ACCEPT_ATTRIBUTE } from '../utils/fileAcceptance';
import {
  LAB_NOTES_MAX_LENGTH,
  ROLL_NAME_MAX_LENGTH,
  validateRollName,
  deriveRollProcessingState,
  canRestartRollProcessing,
} from '../utils/rollUtils';
import type { Roll } from '../types/roll';
import { RollDetails } from './RollDetails';
import styles from './RollCard.module.css';

export interface RollCardScan {
  scan_id: string;
  filename?: string;
  original_filename?: string;
  thumbnail_ready: boolean;
  thumbnail_url?: string;
  roll_id?: string;
  uploaded_at?: string;
  /** Set when the uploaded master could not be read; the fix is a re-upload. */
  master_error?: string;
  /**
   * Whether the master file is actually in S3. A scan record exists from the
   * moment an upload is prepared, so `false` means the upload never finished.
   * Absent when the server couldn't check (treated as uploaded).
   */
  master_uploaded?: boolean;
}

/** Aggregate upload state a card reports up so the order-level footer can gate Confirm. */
export interface RollCardStatus {
  uploading: boolean;
  pending: number;
  failed: number;
}

export interface RollCardProps {
  orderId: string;
  /** The roll, with its booked-in details when it has them. */
  roll: Pick<Roll, 'roll_id' | 'roll_name'> & Partial<Roll>;
  /** Persisted scans belonging to this roll (already filtered by the parent). */
  scans: RollCardScan[];
  /** Read-only cards (order already marked ready, or the "Unassigned" bucket) hide the uploader. */
  readOnly?: boolean;
  /**
   * Whether this card offers the roll-record fields the lab writes for the
   * customer — renaming, the notes editor and the "no recoverable images"
   * checkbox. False for the synthetic "Unassigned" bucket, which has no roll
   * record behind it.
   *
   * Deliberately independent of `readOnly`: the commonest time to write a note
   * is after the order has shipped, when the customer asks why a roll came out
   * the way it did.
   */
  allowLabFields?: boolean;
  onStatusChange?: (rollId: string, status: RollCardStatus) => void;
  /**
   * Controlled open/closed state. When supplied the card renders whatever the
   * parent says and reports header clicks through `onExpandedChange`, which is
   * what lets the page offer an "expand/collapse all" control across cards.
   * Omit it and the card keeps its own state, open by default.
   */
  expanded?: boolean;
  onExpandedChange?: (rollId: string, expanded: boolean) => void;
}

/**
 * The upload surface for a single roll: its file selector, Upload/Retry
 * buttons, per-file progress, and the roll's persisted scans.
 *
 * The files themselves live in the app-wide upload queue (UploadQueueContext),
 * scoped to this roll, not in the card — so they outlive the card being
 * unmounted by a session-expiry redirect or a trip to another page.
 */
export function RollCard({
  orderId,
  roll,
  scans,
  readOnly = false,
  allowLabFields = true,
  onStatusChange,
  expanded: expandedProp,
  onExpandedChange,
}: RollCardProps) {
  const { confirm, confirmDialog } = useConfirm();

  const uploads = useRollUploads(orderId, roll.roll_id);
  /** Feedback about files turned away at selection time (wrong type, duplicates). */
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [expandedState, setExpandedState] = useState(true);
  const expanded = expandedProp ?? expandedState;
  const toggleExpanded = () => {
    const next = !expanded;
    if (expandedProp === undefined) setExpandedState(next);
    onExpandedChange?.(roll.roll_id, next);
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetRoll = useResetRollMutation(orderId);
  const deleteRoll = useDeleteRollMutation(orderId);
  const confirmRoll = useConfirmRollMutation(orderId);
  const updateNotes = useUpdateRollNotesMutation(orderId);
  const updateEmpty = useUpdateRollEmptyMutation(orderId);
  const renameRoll = useRenameRollMutation(orderId);

  // Inline rename: the header swaps its title for an input while editing.
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(roll.roll_name);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameError = validateRollName(nameDraft);
  const nameUnchanged = nameDraft.trim() === roll.roll_name;

  useEffect(() => {
    if (renaming) nameInputRef.current?.select();
  }, [renaming]);

  const startRename = () => {
    renameRoll.reset();
    setNameDraft(roll.roll_name);
    setRenaming(true);
  };

  const cancelRename = () => {
    renameRoll.reset();
    setRenaming(false);
  };

  const handleRenameSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (renameRoll.isPending || nameError) return;
    if (nameUnchanged) {
      setRenaming(false);
      return;
    }
    renameRoll.mutate(
      { rollId: roll.roll_id, rollName: nameDraft },
      { onSuccess: () => setRenaming(false) }
    );
  };

  // The textarea is a draft over the persisted note. `savedNotes` is the card's
  // own record of what is stored, advanced either by a successful save or by a
  // refetch bringing down a different value — the mutation's own response is
  // authoritative straight away, so the "Saved" confirmation doesn't have to
  // wait on the parent's rolls query to come back round.
  const persistedNotes = roll.lab_notes ?? '';
  const [savedNotes, setSavedNotes] = useState(persistedNotes);
  const [notesDraft, setNotesDraft] = useState(persistedNotes);
  const [notesSaved, setNotesSaved] = useState(false);
  const lastPersistedNotesRef = useRef(persistedNotes);

  useEffect(() => {
    // Only a *changed* persisted value resets the draft, so a routine refetch
    // never wipes out whatever the admin is halfway through typing.
    if (lastPersistedNotesRef.current === persistedNotes) return;
    lastPersistedNotesRef.current = persistedNotes;
    setSavedNotes(persistedNotes);
    setNotesDraft(persistedNotes);
  }, [persistedNotes]);

  const notesDirty = notesDraft.trim() !== savedNotes.trim();
  const notesTooLong = notesDraft.trim().length > LAB_NOTES_MAX_LENGTH;

  const handleSaveNotes = () => {
    if (!notesDirty || notesTooLong || updateNotes.isPending) return;
    setNotesSaved(false);
    updateNotes.mutate(
      { rollId: roll.roll_id, labNotes: notesDraft },
      {
        onSuccess: (stored) => {
          // Reflect exactly what was persisted (trimmed), not the raw draft.
          const next = stored ?? '';
          lastPersistedNotesRef.current = next;
          setSavedNotes(next);
          setNotesDraft(next);
          setNotesSaved(true);
        },
      }
    );
  };

  const jobs = uploads.jobs;
  const pendingCount = jobs.filter((j) => j.status === 'pending').length;
  const failedCount = jobs.filter((j) => j.status === 'failed').length;
  const activeJobs = jobs.filter((j) => ACTIVE_STATUSES.has(j.status));
  const uploading = activeJobs.length > 0;
  const uploadedCount = jobs.filter((j) => j.status === 'done').length;
  const skippedCount = jobs.filter((j) => j.status === 'skipped').length;
  // Finished files stay out of the working list; they're summarised instead.
  const listedJobs = jobs.filter((j) => j.status !== 'done' && j.status !== 'skipped');

  // Report aggregate status up so the parent can enable/disable order-level Confirm.
  useEffect(() => {
    onStatusChange?.(roll.roll_id, { uploading, pending: pendingCount, failed: failedCount });
  }, [roll.roll_id, uploading, pendingCount, failedCount, onStatusChange]);

  const handleImageError = useCallback((scanId: string) => {
    setFailedImages((prev) => new Set(prev).add(scanId));
  }, []);

  /**
   * The single entry point into the upload queue — both the file picker and the
   * drop zone route through here so the two behave identically.
   */
  const enqueueFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      const finished = jobs.filter((j) => j.status === 'done' || j.status === 'skipped');
      const result = acceptFiles(
        incoming,
        jobs.filter((j) => !finished.includes(j)).map((j) => j.file),
        finished.map((j) => j.file)
      );
      setSelectionNotice(describeRejections(result));
      uploads.add(result.accepted);
    },
    [jobs, uploads]
  );

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    enqueueFiles(Array.from(selected));
    // Allow re-selecting the same file(s) after a clear.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Files can be added while others upload — they join the same queue.
  const { isDragActive, isReading, dropHandlers } = useFileDrop({
    onFiles: enqueueFiles,
    disabled: readOnly,
  });

  const handleUpload = () => {
    // The notice describes the last selection, not this upload.
    setSelectionNotice(null);
    uploads.start();
  };

  const rollBusy = resetRoll.isPending || deleteRoll.isPending || confirmRoll.isPending;
  const rollActionError = resetRoll.error?.message ?? deleteRoll.error?.message ?? null;

  const handleResetRoll = async () => {
    if (rollBusy) return;
    const ok = await confirm({
      title: 'Reset roll',
      message:
        `Reset roll "${roll.roll_name}"?\n\nThis permanently deletes all its uploaded scans and ` +
        'generated images but keeps the roll itself. This cannot be undone.',
      confirmLabel: 'Reset',
      destructive: true,
    });
    if (ok) resetRoll.mutate(roll.roll_id);
  };

  const handleDeleteRoll = async () => {
    if (rollBusy) return;
    const ok = await confirm({
      title: 'Delete roll',
      message:
        `Delete roll "${roll.roll_name}"?\n\nThis permanently deletes the roll and all its uploaded ` +
        'scans and generated images. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (ok) deleteRoll.mutate(roll.roll_id);
  };

  // A record whose file never reached S3 isn't a scan yet: it doesn't count,
  // can't be confirmed, and shouldn't make a confirmed roll look "processing".
  const uploadedScans = scans.filter((s) => s.master_uploaded !== false);
  const scanCount = uploadedScans.length;
  const activeNames = new Set(activeJobs.map((j) => j.file.name));

  // The checkbox follows the persisted flag, except while a toggle is in flight
  // — then it shows what was asked for, so the tick doesn't spring back and
  // forth between the click and the refetch.
  const isEmpty = updateEmpty.isPending
    ? (updateEmpty.variables?.isEmpty ?? false)
    : (roll.is_empty ?? false);
  // "Empty" and "has images" are contradictory, and the server refuses the
  // combination — so scans on the roll have to go before the box can be ticked.
  const canToggleEmpty = !updateEmpty.isPending && (isEmpty || scans.length === 0);

  const handleToggleEmpty = (e: ChangeEvent<HTMLInputElement>) => {
    if (updateEmpty.isPending) return;
    updateEmpty.mutate({ rollId: roll.roll_id, isEmpty: e.target.checked });
  };

  const bodyId = `roll-card-body-${roll.roll_id}`;
  const emptyFieldId = `roll-card-empty-${roll.roll_id}`;
  const emptyHintId = `roll-card-empty-hint-${roll.roll_id}`;
  const notesFieldId = `roll-card-notes-${roll.roll_id}`;
  const notesHintId = `roll-card-notes-hint-${roll.roll_id}`;
  const dropzoneHintId = `roll-card-dropzone-hint-${roll.roll_id}`;

  const confirmed = roll.confirmed ?? false;
  // The flag alone doesn't say a run is in flight — a roll reset out from under
  // its confirmation has nothing left to generate. See deriveRollProcessingState.
  const rollState = deriveRollProcessingState(confirmed, uploadedScans);
  // Scans whose master could not be read. These never become ready, so without
  // calling them out the card would sit at "Processing…" indefinitely.
  const unreadableScans = scans.filter((s) => s.master_error);
  const canConfirm = rollState === 'awaiting' && scanCount > 0 && pendingCount === 0 && !uploading;
  // The way out of a run that never finished, whether because the workflow
  // failed part-way or because these scans landed after the roll was confirmed
  // and no run has ever covered them.
  const canRestart = canRestartRollProcessing(confirmed, uploadedScans) && !uploading && pendingCount === 0;

  const handleConfirmRoll = () => {
    if (confirmRoll.isPending) return;
    confirmRoll.mutate(roll.roll_id);
  };

  const handleRestartProcessing = async () => {
    if (confirmRoll.isPending) return;
    const ok = await confirm({
      title: 'Restart processing',
      message:
        `Start thumbnail generation again for roll "${roll.roll_name}"?\n\nThis re-runs the ` +
        'workflow over every scan currently on the roll. Nothing is deleted. If the roll is ' +
        'still processing normally, this just duplicates work.',
      confirmLabel: 'Restart',
    });
    if (ok) confirmRoll.mutate(roll.roll_id);
  };

  // Transient upload hints take priority; otherwise show the roll's lifecycle
  // status so a collapsed card still reveals what stage the roll is at.
  const uploadHint = uploads.authPaused && uploading
    ? 'Paused — sign in'
    : uploading
    ? `Uploading ${activeJobs.length}…`
    : failedCount > 0
      ? `${failedCount} failed`
      : pendingCount > 0
        ? `${pendingCount} pending`
        : null;
  const rollStatusHint =
    rollState === 'unreadable'
      ? `${unreadableScans.length} to re-upload`
      : rollState === 'ready'
        ? 'Ready'
        : rollState === 'processing'
          ? 'Processing…'
          : null;
  const statusHint = uploadHint ?? rollStatusHint;

  return (
    <section className={styles.card} aria-label={`Roll ${roll.roll_name}`}>
      <header className={styles.header}>
        {renaming ? (
          <form className={styles.renameForm} onSubmit={handleRenameSubmit}>
            <input
              ref={nameInputRef}
              type="text"
              className={styles.renameInput}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') cancelRename();
              }}
              maxLength={ROLL_NAME_MAX_LENGTH}
              disabled={renameRoll.isPending}
              aria-label={`New name for roll ${roll.roll_name}`}
              aria-invalid={nameError ? true : undefined}
            />
            <button
              type="submit"
              className={styles.saveNotesBtn}
              disabled={!!nameError || renameRoll.isPending}
            >
              {renameRoll.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className={styles.renameCancelBtn}
              onClick={cancelRename}
              disabled={renameRoll.isPending}
            >
              Cancel
            </button>
            {(renameRoll.error || nameError) && (
              <p className={styles.renameError} role="alert">
                {renameRoll.error?.message ?? nameError}
              </p>
            )}
          </form>
        ) : (
          <div className={styles.headerRow}>
            <h3 className={styles.rollHeading}>
              <button
                type="button"
                className={styles.toggle}
                aria-expanded={expanded}
                aria-controls={bodyId}
                onClick={toggleExpanded}
              >
                <span
                  className={`${styles.chevron}${expanded ? ` ${styles.chevronOpen}` : ''}`}
                  aria-hidden="true"
                />
                <span className={styles.rollName}>{roll.roll_name}</span>
                <span className={styles.scanCount}>
                  {scanCount} scan{scanCount === 1 ? '' : 's'}
                </span>
                {isEmpty && <span className={styles.emptyBadge}>Empty roll</span>}
                {savedNotes.trim() !== '' && (
                  <span className={styles.notesBadge}>Lab note</span>
                )}
                {statusHint && <span className={styles.statusHint}>{statusHint}</span>}
              </button>
            </h3>
            {allowLabFields && (
              <button
                type="button"
                className={styles.renameBtn}
                onClick={startRename}
              >
                Rename
              </button>
            )}
          </div>
        )}
      </header>

      <div id={bodyId} className={styles.body} hidden={!expanded}>
      {allowLabFields && roll.roll_type !== 'edits' && <RollDetails orderId={orderId} roll={roll} />}

      {allowLabFields && (
        <div className={styles.emptyToggle}>
          <label className={styles.emptyToggleLabel} htmlFor={emptyFieldId}>
            <input
              id={emptyFieldId}
              type="checkbox"
              className={styles.emptyCheckbox}
              checked={isEmpty}
              onChange={handleToggleEmpty}
              disabled={!canToggleEmpty}
              aria-describedby={emptyHintId}
            />
            This roll came back empty — no recoverable images
          </label>
          <p id={emptyHintId} className={styles.emptyToggleHint}>
            Tick this when the film was developed but there was nothing to scan.
            The customer still sees the roll and your lab notes, with no images
            against it.
            {scans.length > 0 && !isEmpty && (
              <> Reset the roll to remove its {scans.length} uploaded scan
                {scans.length === 1 ? '' : 's'} first.</>
            )}
          </p>
          {updateEmpty.error && (
            <p className={styles.errorText} role="alert">
              {updateEmpty.error.message}
            </p>
          )}
        </div>
      )}

      {allowLabFields && (
        <div className={styles.notes}>
          <label htmlFor={notesFieldId} className={styles.notesLabel}>
            Lab notes
          </label>
          <p id={notesHintId} className={styles.notesHint}>
            Shown to the customer against this roll — e.g. “this roll came out very dark
            because the film was well past expiry”. Leave blank for no note.
          </p>
          <textarea
            id={notesFieldId}
            className={styles.notesInput}
            value={notesDraft}
            onChange={(e) => {
              setNotesDraft(e.target.value);
              // "Saved" describes the last write; any edit supersedes it, even
              // one that later returns to the saved text.
              setNotesSaved(false);
            }}
            rows={3}
            maxLength={LAB_NOTES_MAX_LENGTH}
            disabled={updateNotes.isPending}
            aria-describedby={notesHintId}
            placeholder="Add a note the customer will see with this roll"
          />
          <div className={styles.notesActions}>
            <button
              type="button"
              className={styles.saveNotesBtn}
              onClick={handleSaveNotes}
              disabled={!notesDirty || notesTooLong || updateNotes.isPending}
            >
              {updateNotes.isPending ? 'Saving…' : 'Save notes'}
            </button>
            {notesDirty && !updateNotes.isPending && (
              <span className={styles.notesStatus}>Unsaved changes</span>
            )}
            {notesSaved && !notesDirty && (
              <span className={styles.notesSavedStatus} role="status">
                Saved
              </span>
            )}
          </div>
          {updateNotes.error && (
            <p className={styles.errorText} role="alert">
              {updateNotes.error.message}
            </p>
          )}
        </div>
      )}

      {!readOnly && !isEmpty && (
        <div className={styles.uploader}>
          {/*
            The label doubles as the drop zone: clicking or keyboard-activating
            it opens the picker, while dragging files onto it queues them
            directly. The input stays in the DOM (visually hidden, not
            `display: none`) so it keeps its accessible name and tab stop.
          */}
          <label
            className={`${styles.dropzone}${isDragActive ? ` ${styles.dropzoneActive}` : ''}`}
            data-drag-active={isDragActive ? 'true' : undefined}
            {...dropHandlers}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              multiple
              onChange={handleFileSelect}
              className={styles.fileInput}
              aria-label={`Select files for ${roll.roll_name}`}
              aria-describedby={dropzoneHintId}
            />
            <span className={styles.dropzoneText} aria-hidden="true">
              {isReading
                ? 'Reading folder…'
                : isDragActive
                  ? 'Drop to add files'
                  : 'Drag scans or a folder here, or click to browse'}
            </span>
            <span id={dropzoneHintId} className={styles.dropzoneHint}>
              TIFF only (.tif, .tiff)
            </span>
          </label>

          {selectionNotice && (
            <p className={styles.noticeText} role="alert">
              {selectionNotice}
            </p>
          )}

          {(uploadedCount > 0 || skippedCount > 0) && (
            <p className={styles.uploadSummary} role="status">
              {[
                uploadedCount > 0 && `${uploadedCount} uploaded`,
                skippedCount > 0 && `${skippedCount} already on the server — skipped`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}

          {uploads.authPaused && uploading && (
            <p className={styles.noticeText} role="alert">
              Your session expired. Uploads are paused and carry on once you sign in again.
            </p>
          )}

          {listedJobs.length > 0 && (
            <ul className={styles.fileList}>
              {listedJobs.map((job) => (
                <li key={job.id} className={styles.fileItem}>
                  <div className={styles.fileRow}>
                    {job.status === 'uploading' ? (
                      <span className={styles.fileProgressLabel}>{job.progress}%</span>
                    ) : (
                      <span className={styles.fileStatus} style={{ color: statusColor(job) }}>
                        [{statusLabel(job)}]
                      </span>
                    )}
                    <span className={styles.fileName}>{job.file.name}</span>
                    <span className={styles.fileSize}>({formatSize(job.file.size)})</span>
                    {(job.status === 'pending' || job.status === 'failed') && (
                      <button
                        type="button"
                        className={styles.removeFileBtn}
                        onClick={() => uploads.remove(job.id)}
                        aria-label={`Remove ${job.file.name}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {job.status === 'uploading' && (
                    <progress
                      className={styles.uploadProgress}
                      value={job.progress}
                      max={100}
                      aria-label={`${job.file.name}: ${job.progress}% uploaded`}
                    />
                  )}
                  {job.error && (job.status === 'failed' || job.status === 'waiting') && (
                    <span className={styles.fileError}>{job.error}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className={styles.actions}>
            {pendingCount > 0 && (
              <button onClick={handleUpload}>
                {`Upload ${pendingCount} file${pendingCount === 1 ? '' : 's'}`}
              </button>
            )}
            {failedCount > 0 && (
              <button onClick={uploads.retryFailed}>Retry Failed ({failedCount})</button>
            )}
          </div>
        </div>
      )}

      {scans.length === 0 ? (
        <p className={styles.emptyRollMessage}>
          {isEmpty
            ? 'Marked empty — the customer sees this roll with no images.'
            : 'No scans uploaded yet'}
        </p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Thumbnail</th>
                <th>Filename</th>
                <th>Thumbnail Status</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan) => (
                <tr key={scan.scan_id}>
                  <td>
                    {scan.thumbnail_ready && scan.thumbnail_url && !failedImages.has(scan.scan_id) ? (
                      <img
                        src={scan.thumbnail_url}
                        alt={scan.original_filename || scan.filename || 'Scan thumbnail'}
                        className={styles.thumbnail}
                        loading="lazy"
                        onError={() => handleImageError(scan.scan_id)}
                      />
                    ) : (
                      <span className={styles.thumbnailPlaceholder}>
                        {failedImages.has(scan.scan_id) ? 'Unavailable' : '—'}
                      </span>
                    )}
                  </td>
                  <td>{scan.original_filename || scan.filename}</td>
                  <td>
                    {scan.master_uploaded === false ? (
                      activeNames.has(scan.original_filename ?? scan.filename ?? '') ? (
                        <span className={styles.statusProcessing}>Uploading</span>
                      ) : (
                        <span
                          className={styles.statusFailed}
                          title="The upload of this file never finished. Add it again to upload it."
                        >
                          Not uploaded
                        </span>
                      )
                    ) : scan.master_error ? (
                      <span className={styles.statusFailed} title={scan.master_error}>
                        Unreadable — re-upload
                      </span>
                    ) : scan.thumbnail_ready ? (
                      <span className={styles.statusReady}>Ready</span>
                    ) : (
                      <span className={styles.statusProcessing}>Processing</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && rollState === 'awaiting' && scanCount > 0 && (
        <div className={styles.confirmRow}>
          <button
            type="button"
            className={styles.confirmRollBtn}
            onClick={handleConfirmRoll}
            disabled={!canConfirm || confirmRoll.isPending}
          >
            {confirmRoll.isPending ? 'Confirming…' : 'Confirm uploads'}
          </button>
          {pendingCount > 0 && (
            <span className={styles.confirmHint}>Upload pending files first</span>
          )}
          {confirmRoll.error && (
            <span className={styles.errorText} role="alert">
              {confirmRoll.error.message}
            </span>
          )}
        </div>
      )}

      {!readOnly && rollState !== 'awaiting' && (
        <div className={styles.confirmRow}>
          <p className={styles.processingNote}>
            {rollState === 'unreadable' ? (
              <span className={styles.errorText} role="alert">
                {unreadableScans.length === 1
                  ? '1 file could not be read and must be uploaded again: '
                  : `${unreadableScans.length} files could not be read and must be uploaded again: `}
                {unreadableScans
                  .map((s) => s.original_filename || s.filename)
                  .join(', ')}
              </span>
            ) : rollState === 'ready' ? (
              'Confirmed — all thumbnails ready.'
            ) : (
              'Confirmed — generating thumbnails…'
            )}
          </p>
          {canRestart && (
            <button
              type="button"
              className={styles.confirmRollBtn}
              onClick={handleRestartProcessing}
              disabled={confirmRoll.isPending}
            >
              {confirmRoll.isPending ? 'Restarting…' : 'Restart processing'}
            </button>
          )}
          {confirmRoll.error && (
            <span className={styles.errorText} role="alert">
              {confirmRoll.error.message}
            </span>
          )}
        </div>
      )}

      {!readOnly && (
        <div className={styles.rollActions}>
          <button
            type="button"
            className={styles.resetRollBtn}
            onClick={handleResetRoll}
            disabled={rollBusy || (scans.length === 0 && !confirmed)}
          >
            {resetRoll.isPending ? 'Resetting…' : 'Reset roll'}
          </button>
          <button
            type="button"
            className={styles.deleteRollBtn}
            onClick={handleDeleteRoll}
            disabled={rollBusy}
          >
            {deleteRoll.isPending ? 'Deleting…' : 'Delete roll'}
          </button>
          {rollActionError && (
            <p className={styles.errorText} role="alert">
              {rollActionError}
            </p>
          )}
        </div>
      )}
      </div>
      {confirmDialog}
    </section>
  );
}

// --- Local helpers ---

function statusLabel(job: UploadJob): string {
  switch (job.status) {
    case 'checking':
      return 'checking';
    case 'waiting':
      return 'retrying';
    default:
      return job.status;
  }
}

function statusColor(job: UploadJob): string {
  switch (job.status) {
    case 'failed':
      return 'var(--colour-error)';
    case 'waiting':
      return 'var(--colour-warning)';
    case 'checking':
    case 'uploading':
      return 'var(--colour-info)';
    default:
      return 'var(--colour-text-muted)';
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
